import { createPortal } from "react-dom";
import styled from "styled-components";
import { useNotesModal } from "../../context/NotesModalContext";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { collection, query, getDocs, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  CalendarDays,
  NotebookPen,
  Sparkles,
  X,
} from "lucide-react";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.7);
  backdrop-filter: blur(8px);
  z-index: 2000;
`;

const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  margin: auto;
  width: min(760px, calc(100vw - 32px));
  height: min(760px, calc(100vh - 40px));
  padding: 22px;
  border-radius: 30px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.94), rgba(2, 6, 23, 0.92)),
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.1));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 34px 100px rgba(0, 0, 0, 0.58);
  color: #e5e7eb;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 640px) {
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #04111f;
`;

const HeaderCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Title = styled.h3`
  margin-top: 12px;
  font-size: clamp(24px, 3vw, 32px);
  line-height: 1.05;
  font-weight: 1000;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin-top: 10px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.65;
  max-width: 520px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.5);
  color: #dbeafe;
  font-weight: 800;
  cursor: pointer;
`;

const CloseBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.5);
  color: #94a3b8;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const Body = styled.div`
  margin-top: 18px;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const List = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
`;

const NoteItem = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.12);
  cursor: pointer;
  text-align: left;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(56, 189, 248, 0.22);
    background: rgba(15, 23, 42, 0.88);
  }
`;

const NotePreview = styled.div`
  font-size: 15px;
  line-height: 1.65;
  color: #e2e8f0;
`;

const NoteMetaRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;

const DetailCard = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const DetailText = styled.div`
  font-size: 15px;
  line-height: 1.8;
  color: #e2e8f0;
  white-space: pre-wrap;
`;

const ComposeWrap = styled.div`
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TextArea = styled.textarea`
  min-height: 0;
  flex: 1;
  resize: none;
  border-radius: 22px;
  padding: 18px;
  font-size: 15px;
  line-height: 1.7;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.7);
  color: #e5e7eb;
  outline: none;

  &::placeholder {
    color: #64748b;
  }
`;

const ComposerMeta = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #94a3b8;
  font-size: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const SaveBtn = styled.button`
  margin-top: 14px;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: 999px;
  font-weight: 900;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #04111f;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
`;

const EmptyCard = styled.div`
  width: min(100%, 420px);
  padding: 28px 24px;
  border-radius: 24px;
  text-align: center;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);

  strong {
    display: block;
    margin-top: 14px;
    color: #f8fafc;
    font-size: 18px;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #94a3b8;
    font-size: 14px;
    line-height: 1.65;
  }
`;

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(30, 41, 59, 0.86) 0%,
    rgba(51, 65, 85, 0.96) 50%,
    rgba(30, 41, 59, 0.86) 100%
  );
  background-size: 200% 100%;
  animation: notesShimmer 1.5s linear infinite;

  @keyframes notesShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonNote = styled.div`
  padding: 16px;
  border-radius: 20px;
  ${shimmer}
`;

const SkeletonLine = styled.div`
  height: ${({ h }) => h || 12}px;
  width: ${({ w }) => w || "100%"};
  border-radius: 8px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.08);
`;

export default function SavedNotesModal() {
  const { open, setOpen } = useNotesModal();
  const { user } = useAuth();
  const [mode, setMode] = useState("list");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNote, setActiveNote] = useState(null);

  const resetState = () => {
    setMode("list");
    setActiveNote(null);
    setNewNote("");
  };

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open || !user?.uid) return;

    async function loadNotes() {
      setLoading(true);
      const q = query(collection(db, "users", user.uid, "notes"));
      const snap = await getDocs(q);

      const nextNotes = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || 0;
          const tb = b.createdAt?.toMillis?.() || 0;
          return tb - ta;
        });

      setNotes(nextNotes);
      setLoading(false);
    }

    loadNotes();
  }, [open, user?.uid]);

  if (!open) return null;

  const closeModal = () => {
    setOpen(false);
    resetState();
  };

  const NotesSkeleton = () => (
    <>
      {[1, 2, 3, 4].map((i) => (
        <SkeletonNote key={i}>
          <SkeletonLine w="88%" />
          <SkeletonLine w="74%" />
          <SkeletonLine w="42%" h={10} />
        </SkeletonNote>
      ))}
    </>
  );

  const renderContent = () => {
    if (mode === "create") {
      return (
        <ComposeWrap>
          <TextArea
            placeholder="Write the insight, lesson, blocker, or reminder you want to keep close..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            autoFocus
          />

          <ComposerMeta>
            <span>Use notes to save important mentor replies, roadmap insights, and next steps.</span>
            <span>{newNote.trim().length} characters</span>
          </ComposerMeta>

          <SaveBtn
            disabled={!newNote.trim() || saving}
            onClick={async () => {
              if (!newNote.trim()) return;

              setSaving(true);
              await addDoc(collection(db, "users", user.uid, "notes"), {
                content: newNote.trim(),
                createdAt: new Date(),
              });

              const snap = await getDocs(collection(db, "users", user.uid, "notes"));
              const nextNotes = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort(
                  (a, b) =>
                    (b.createdAt?.toMillis?.() || 0) -
                    (a.createdAt?.toMillis?.() || 0)
                );

              setNotes(nextNotes);
              setNewNote("");
              setMode("list");
              setSaving(false);
            }}
          >
            {saving ? "Saving..." : "Save Note"}
          </SaveBtn>
        </ComposeWrap>
      );
    }

    if (activeNote) {
      return (
        <DetailCard>
          <NoteMetaRow style={{ marginTop: 0, marginBottom: 14 }}>
            <MetaPill>
              <BookOpen size={14} />
              {activeNote.relatedDay ? `Day ${activeNote.relatedDay}` : "General note"}
            </MetaPill>
            <MetaPill>
              <CalendarDays size={14} />
              Saved insight
            </MetaPill>
          </NoteMetaRow>
          <DetailText>{activeNote.content}</DetailText>
        </DetailCard>
      );
    }

    if (loading) {
      return <List><NotesSkeleton /></List>;
    }

    if (!notes.length) {
      return (
        <Empty>
          <EmptyCard>
            <NotebookPen size={24} color="#7dd3fc" />
            <strong>No notes saved yet</strong>
            <span>
              Save mentor replies, roadmap insights, and your own reflections so the important parts stay easy to revisit.
            </span>
          </EmptyCard>
        </Empty>
      );
    }

    return (
      <List>
        {notes.map((n) => (
          <NoteItem key={n.id} onClick={() => setActiveNote(n)}>
            <NotePreview>
              {n.content.slice(0, 160)}
              {n.content.length > 160 ? "..." : ""}
            </NotePreview>

            <NoteMetaRow>
              <MetaPill>
                <BookOpen size={14} />
                {n.relatedDay ? `Day ${n.relatedDay}` : "General note"}
              </MetaPill>
              <MetaPill>
                <CalendarDays size={14} />
                {n.createdAt?.toDate?.()
                  ? n.createdAt.toDate().toLocaleDateString()
                  : "Recently saved"}
              </MetaPill>
            </NoteMetaRow>
          </NoteItem>
        ))}
      </List>
    );
  };

  return createPortal(
    <>
      <Overlay onClick={closeModal} />
      <Modal
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
      >
        <Header>
          <HeaderLeft>
            <HeaderIcon>
              <NotebookPen size={20} />
            </HeaderIcon>
            <HeaderCopy>
              <Eyebrow>
                <Sparkles size={12} />
                Knowledge Base
              </Eyebrow>
              <Title>
                {mode === "create"
                  ? "Create a new note"
                  : activeNote
                  ? "Saved note"
                  : "Your saved notes"}
              </Title>
              <Sub>
                Keep the useful parts of your roadmap, mentor replies, blockers, and important ideas in one place you can revisit fast.
              </Sub>
            </HeaderCopy>
          </HeaderLeft>

          <HeaderActions>
            {(activeNote || mode === "create") && (
              <GhostBtn
                onClick={() => {
                  resetState();
                }}
              >
                <ArrowLeft size={16} />
                Back
              </GhostBtn>
            )}

            {mode === "list" && (
              <GhostBtn onClick={() => setMode("create")}>
                <NotebookPen size={16} />
                Add note
              </GhostBtn>
            )}

            <CloseBtn onClick={closeModal}>
              <X size={18} />
            </CloseBtn>
          </HeaderActions>
        </Header>

        <Body>{renderContent()}</Body>
      </Modal>
    </>,
    document.body
  );
}
